import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Database, Plus, X, Save, Loader2, Trash2, Edit2, HelpCircle, Lock, Upload, Download, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { useActiveCompany } from '../hooks/useActiveCompany';
import { formatApiError } from '../lib/errorUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Short descriptions displayed in the help tooltip (? icon) next to each master.
// These help tenant admins understand what the master controls and why it matters.
const MASTER_HELP = {
  legal_entities: 'Registered legal entities (e.g. "HECS Holdings Pvt Ltd"). Used on payslips, PF/ESI returns, and tax filings.',
  designations: 'Job titles like "Senior Engineer", "HR Manager". Used in offer letters, profile and payslip header.',
  grades: 'Pay bands (G1, G2, G3). Validates salary ranges during hiring, controls approval matrix.',
  branches: 'Office / regional branches. Used for location-wise attendance and compliance reporting.',
  cost_centers: 'Finance cost center codes. Used to tag payroll expense into GL during book-keeping.',
  employee_categories: 'Groups like Regular / Contract / Trainee. Drives eligibility for benefits and statutory rules. Can also define a Wage Period cycle (e.g. 21st → 20th) — attendance uploads use it to map dates to the correct payroll period.',
  employment_types: 'Permanent / Contract / Temporary. Controls PF/ESI deduction + contract expiry tracking.',
  worker_types: 'Staff / Worker / Executive classification used for shift assignment and minimum wage compliance.',
  shift_configs: 'Working hours definition (09:00-18:00 etc.). Drives attendance, overtime calculation, and late marks.',
  holiday_calendars: 'Company-wide paid holidays per year. Excluded from attendance deductions and LOP calculation.',
  leave_types: 'Leave types (CL/SL/EL/ML/PL/CO/LOP) with yearly quota, carry-forward, encashment, and medical-cert rules. Drives leave request + balance computation.',
  leave_policies: 'Legacy leave policy master. Use Leave Types instead (below) for new setups.',
  salary_heads: 'Components of a payslip — Earnings (Basic/HRA) and Deductions (PF/PT). Defines formula + statutory tag.',
  salary_structures: 'Template that bundles salary heads with default %/amounts. Applied when assigning CTC to an employee.',
  reimbursement_heads: 'Claim categories (Fuel, Travel, Internet). Controls claim form fields + approver matrix.',
  advance_types: 'Types of salary advances (Festival, Medical). Defines max amount, EMI recovery rule.',
  statutory_setups: 'PF / ESI / PT / TDS configuration (rates + registration numbers). Used by statutory reports.',
  banks: 'Bank list for salary disbursement + IFSC validation on bank details capture.',
  document_types: 'Documents to collect from employees (Aadhaar, PAN, Passport). Drives onboarding checklist.',
};

const ordSuffix = (n) => {
  const v = Number(n);
  if (!v) return '';
  const s = ['th', 'st', 'nd', 'rd'], r = v % 100;
  return `${v}${s[(r - 20) % 10] || s[r] || s[0]}`;
};

const Masters = () => {
  const { user: currentUser } = useAuth();
  const canEdit = (currentUser?.roles && currentUser.roles.includes('tenant_admin'))
    || currentUser?.role === 'tenant_admin';
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'organization';
  const focusKey = searchParams.get('focus');
  useEffect(() => {
    if (!focusKey) return;
    const el = document.querySelector(`[data-testid="master-card-${focusKey}"]`);
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-[#0052FF]');
        setTimeout(() => el.classList.remove('ring-2', 'ring-[#0052FF]'), 2500);
      }, 400);
    }
  }, [focusKey]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeMaster, setActiveMaster] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', code: '', description: '', is_active: true });
  const [masterData, setMasterData] = useState({});
  const [loading, setLoading] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [cardSearch, setCardSearch] = useState({}); // per-card search query keyed by item.key

  const masterCategories = [
    {
      id: 'organization', label: 'Organization',
      items: [
        { name: 'Legal Entities', key: 'legal_entities', collection: 'legal_entities' },
        { name: 'Designations', key: 'designations', collection: 'designations' },
        { name: 'Grades & Bands', key: 'grades', collection: 'grades' },
        { name: 'Branches', key: 'branches', collection: 'branches' },
        { name: 'Cost Centers', key: 'cost_centers', collection: 'cost_centers' },
      ],
    },
    {
      id: 'employee', label: 'Employee',
      items: [
        { name: 'Employee Categories', key: 'employee_categories', collection: 'employee_categories' },
        { name: 'Employment Types', key: 'employment_types', collection: 'employment_types' },
        { name: 'Worker Types', key: 'worker_types', collection: 'worker_types' },
      ],
    },
    {
      id: 'attendance', label: 'Attendance',
      items: [
        { name: 'Shift Master', key: 'shift_configs', collection: 'shift_configs' },
        { name: 'Holiday Calendars', key: 'holiday_calendars', collection: 'holiday_calendars' },
        { name: 'Leave Types & Policies', key: 'leave_policies', collection: 'leave_types' },
      ],
    },
    {
      id: 'payroll', label: 'Payroll',
      items: [
        { name: 'Salary Heads', key: 'salary_heads', collection: 'salary_heads' },
        // Salary Structures removed from Masters — managed under Business Controls → Salary Structures (CTC Groups).
        { name: 'Reimbursement Heads', key: 'reimbursement_heads', collection: 'reimbursement_heads' },
        { name: 'Advance Types', key: 'advance_types', collection: 'advance_types' },
      ],
    },
    {
      id: 'statutory', label: 'Statutory',
      items: [
        { name: 'Statutory Setup', key: 'statutory_setups', collection: 'statutory_configs' },
        { name: 'Bank Master', key: 'banks', collection: 'bank_master' },
        { name: 'Document Types', key: 'document_types', collection: 'document_types' },
      ],
    },
  ];

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data);
      setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  const fetchAllMasters = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/masters/all?company_id=${selectedCompany}`, { withCredentials: true });
      setMasterData(data);
    } catch (e) {
      // Fallback: reset to empty
      setMasterData({});
    }
    setLoading(false);
  }, [selectedCompany]);

  useEffect(() => { if (selectedCompany) fetchAllMasters(); }, [selectedCompany, fetchAllMasters]);



  const handleAdd = (item) => {
    setActiveMaster(item);
    setEditingId(null);
    setFormData({
      name: '', code: '', description: '', is_active: true,
      // Employee-category specific (only persisted when item.key === 'employee_categories')
      wage_period_start_day: '',
      wage_period_end_day: '',
      // Salary-head specific defaults (only persisted when item.key === 'salary_heads')
      head_type: 'earning',
      is_attendance_linked: true,
      is_taxable: true,
      is_part_of_ctc: true,
      is_part_of_gross: true,
      is_monthly_input: false,
      affects_net_pay: true,
      show_on_payslip: true,
      pf_applicable: false,
      esi_applicable: false,
      pt_applicable: false,
    });
    setShowModal(true);
  };

  const handleEdit = (item, record) => {
    setActiveMaster(item);
    setEditingId(record.id || record._id);
    setFormData({
      name: record.name || record.shift_name || '',
      code: record.code || '',
      description: record.description || '',
      is_active: record.is_active !== false,
      wage_period_start_day: record.wage_period_start_day ?? '',
      wage_period_end_day: record.wage_period_end_day ?? '',
      head_type: record.head_type || 'earning',
      is_attendance_linked: record.is_attendance_linked !== false,
      is_taxable: record.is_taxable !== false,
      is_part_of_ctc: record.is_part_of_ctc !== false,
      is_part_of_gross: record.is_part_of_gross !== false,
      is_monthly_input: !!record.is_monthly_input,
      affects_net_pay: record.affects_net_pay !== false,
      show_on_payslip: record.show_on_payslip !== false,
      pf_applicable: !!record.pf_applicable,
      esi_applicable: !!record.esi_applicable,
      pt_applicable: !!record.pt_applicable,
    });
    setShowModal(true);
  };

  // ======= Bulk CSV upload =======
  const downloadTemplate = (item) => {
    const headers = ['name', 'code', 'description'];
    const sample = [`Sample ${item.name.slice(0, -1)}`, 'SAMPLE', 'Optional description'];
    const csv = [headers.join(','), sample.join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${item.key}_template.csv`;
    a.click();
  };

  const onCsvUpload = async (item, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) { toast.error('CSV is empty'); e.target.value = ''; return; }
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const rows = lines.slice(1).map(line => {
      const cols = line.split(',').map(v => v.trim());
      return Object.fromEntries(headers.map((h, i) => [h, cols[i] || '']));
    });

    // Ask the admin how to handle duplicates.
    const choice = window.prompt(
      `Found ${rows.length} rows in CSV.\n\n` +
      `How should duplicates (matching code or name) be handled?\n\n` +
      `  1 = SKIP duplicates (default — safest, only adds new rows)\n` +
      `  2 = UPSERT (overwrite name/description on duplicates, insert new)\n` +
      `  3 = REPLACE ALL (DELETE every existing ${item.name.toLowerCase()} first, then insert these — DESTRUCTIVE)\n\n` +
      `Type 1, 2 or 3:`, '1');
    if (choice === null) { e.target.value = ''; return; }
    const mode = ({1: 'skip', 2: 'upsert', 3: 'replace_all'})[String(choice).trim()];
    if (!mode) { toast.error('Invalid choice — upload cancelled'); e.target.value = ''; return; }
    if (mode === 'replace_all' && !window.confirm(
      `⚠️ REPLACE ALL will DELETE every existing ${item.name.toLowerCase()} for this company first, then insert ${rows.length} new rows.\n\nAre you absolutely sure?`)) {
      e.target.value = ''; return;
    }

    try {
      const { data } = await axios.post(`${API_URL}/masters/bulk-upload`, {
        collection: item.collection,
        company_id: selectedCompany,
        rows,
        mode,
      }, { withCredentials: true });
      const parts = [];
      if (data.added) parts.push(`${data.added} added`);
      if (data.updated) parts.push(`${data.updated} updated`);
      if (data.skipped) parts.push(`${data.skipped} skipped`);
      if (data.failed) parts.push(`${data.failed} failed`);
      if (data.deleted) parts.push(`${data.deleted} deleted (replace mode)`);
      toast.success(`[${data.mode}] ${parts.join(' · ')}`);
      if (data.skipped_reasons?.length) console.info('Skipped:', data.skipped_reasons);
      fetchAllMasters();
    } catch (err) {
      toast.error(formatApiError(err) || 'Bulk upload failed');
    }
    e.target.value = '';
  };

  const bulkDeleteAll = async (item) => {
    const list = masterData[item.key] || [];
    if (!list.length) { toast.error(`No ${item.name.toLowerCase()} to delete`); return; }
    const phrase = `delete ${list.length} ${item.name.toLowerCase()}`;
    const confirm1 = window.prompt(
      `⚠️ DESTRUCTIVE ACTION — Bulk delete\n\n` +
      `This will permanently delete ALL ${list.length} ${item.name.toLowerCase()} ` +
      `for the active company. Use this when the data was uploaded incorrectly and you ` +
      `want to wipe the slate before re-uploading.\n\n` +
      `Type the phrase below to confirm:\n\n  ${phrase}`);
    if (!confirm1 || confirm1.trim().toLowerCase() !== phrase.toLowerCase()) {
      toast.info('Bulk delete cancelled');
      return;
    }
    try {
      const { data } = await axios.post(`${API_URL}/masters/bulk-delete`, {
        collection: item.collection,
        company_id: selectedCompany,
        confirm: true,
      }, { withCredentials: true });
      toast.success(`Deleted ${data.deleted} ${item.name.toLowerCase()}`);
      fetchAllMasters();
    } catch (err) {
      toast.error(formatApiError(err) || 'Bulk delete failed');
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        collection: activeMaster.collection,
        company_id: selectedCompany,
        ...formData,
        code: formData.code || formData.name.toUpperCase().replace(/\s+/g, '_').substring(0, 10),
      };
      if (activeMaster.key === 'employee_categories') {
        const s = String(formData.wage_period_start_day ?? '').trim();
        const e = String(formData.wage_period_end_day ?? '').trim();
        if ((s === '') !== (e === '')) {
          toast.error('Set BOTH wage period days, or leave both blank for calendar month');
          setSaving(false);
          return;
        }
        const si = parseInt(s, 10), ei = parseInt(e, 10);
        if (s !== '' && (!(si >= 1 && si <= 31) || !(ei >= 1 && ei <= 31))) {
          toast.error('Wage period days must be between 1 and 31');
          setSaving(false);
          return;
        }
        payload.wage_period_start_day = s === '' ? null : si;
        payload.wage_period_end_day = e === '' ? null : ei;
      } else {
        delete payload.wage_period_start_day;
        delete payload.wage_period_end_day;
      }
      if (editingId) {
        await axios.patch(`${API_URL}/masters/item/${editingId}`, payload, { withCredentials: true });
        toast.success(`${activeMaster.name} updated successfully`);
      } else {
        await axios.post(`${API_URL}/masters/item`, payload, { withCredentials: true });
        toast.success(`${activeMaster.name} added successfully`);
      }
      setShowModal(false);
      setEditingId(null);
      fetchAllMasters();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const handleDelete = async (collection, itemId) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await axios.delete(`${API_URL}/masters/item/${itemId}?collection=${collection}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAllMasters();
    } catch (e) { toast.error('Delete failed'); }
  };

  const getCount = (key) => {
    const items = masterData[key];
    return Array.isArray(items) ? items.length : 0;
  };

  const getItems = (key) => {
    return Array.isArray(masterData[key]) ? masterData[key] : [];
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="masters-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <Database className="inline-block w-8 h-8 mr-2 mb-1" /> Master Data Management
            </h1>
            <p className="mt-1 text-sm text-slate-600">Configure and manage all master data for payroll operations</p>
          </div>
          <select value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)} data-testid="masters-company-select"
            className="h-10 px-3 bg-white border border-slate-300 rounded-md text-sm">
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {!canEdit && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 flex items-center gap-3" data-testid="masters-readonly-banner">
            <Lock className="w-5 h-5 text-amber-600 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">Read-only view</p>
              <p className="text-xs text-amber-700">
                Only a Tenant Admin can create, edit, or delete master records. You can browse existing masters here. Contact your Tenant Admin to make changes.
              </p>
            </div>
          </div>
        )}

        <Tabs defaultValue={activeTab} className="w-full">
          <TabsList className="border-b border-slate-200 w-full justify-start rounded-none bg-transparent p-0">
            {masterCategories.map(c => (
              <TabsTrigger key={c.id} value={c.id} data-testid={`master-tab-${c.id}`}
                className="data-[state=active]:border-b-2 data-[state=active]:border-[#0052FF] data-[state=active]:text-[#0052FF] rounded-none pb-3">
                {c.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {masterCategories.map(category => (
            <TabsContent key={category.id} value={category.id} className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {category.items.map((item) => {
                  const allItems = getItems(item.collection);
                  const count = allItems.length;
                  const q = (cardSearch[item.key] || '').trim().toLowerCase();
                  const items = q
                    ? allItems.filter(r => {
                        const name = (r.name || r.shift_name || '').toLowerCase();
                        const code = (r.code || '').toLowerCase();
                        return name.includes(q) || code.includes(q);
                      })
                    : allItems;
                  return (
                    <div key={item.key} data-testid={`master-card-${item.key}`}
                      className="bg-white border border-slate-200 rounded-lg p-5 hover:shadow-sm transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-slate-900 flex items-center gap-1.5">
                            {item.name}
                            {MASTER_HELP[item.key] && (
                              <span className="relative group">
                                <HelpCircle data-testid={`master-help-${item.key}`} className="w-3.5 h-3.5 text-slate-400 hover:text-[#0052FF] cursor-help" />
                                <span
                                  role="tooltip"
                                  className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-1.5 w-64 px-3 py-2 rounded-md bg-slate-900 text-white text-[11px] leading-relaxed shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-30"
                                >
                                  {MASTER_HELP[item.key]}
                                </span>
                              </span>
                            )}
                          </h3>
                          <p className="text-sm text-slate-500 mt-0.5">{count} {count === 1 ? 'record' : 'records'}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          {canEdit && (
                            <>
                              <button
                                onClick={() => downloadTemplate(item)}
                                data-testid={`template-${item.key}`}
                                title="Download CSV template"
                                className="p-1.5 text-slate-500 hover:text-[#0052FF] hover:bg-slate-50 rounded"
                              >
                                <Download className="w-3.5 h-3.5" />
                              </button>
                              <label
                                title="Bulk upload CSV"
                                data-testid={`upload-${item.key}-label`}
                                className="p-1.5 text-slate-500 hover:text-[#0052FF] hover:bg-slate-50 rounded cursor-pointer"
                              >
                                <Upload className="w-3.5 h-3.5" />
                                <input type="file" accept=".csv" hidden
                                  onChange={(e) => onCsvUpload(item, e)}
                                  data-testid={`upload-${item.key}`} />
                              </label>
                              {allItems.length > 0 && (
                                <button
                                  onClick={() => bulkDeleteAll(item)}
                                  data-testid={`bulk-delete-${item.key}`}
                                  title={`Delete all ${allItems.length} ${item.name.toLowerCase()} (DESTRUCTIVE)`}
                                  className="p-1.5 text-rose-500 hover:text-white hover:bg-rose-600 rounded"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </>
                          )}
                          <Button size="sm" variant="outline" data-testid={`add-${item.key}`}
                            disabled={!canEdit}
                            title={canEdit ? '' : 'Only Tenant Admin can add masters'}
                            onClick={() => canEdit && handleAdd(item)}>
                            <Plus className="w-4 h-4 mr-1" /> Add
                          </Button>
                        </div>
                      </div>
                      {allItems.length > 0 && (
                        <div className="space-y-1.5 mt-3 border-t border-slate-100 pt-3">
                          {allItems.length >= 6 && (
                            <div className="relative mb-1">
                              <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                              <input
                                type="text"
                                value={cardSearch[item.key] || ''}
                                onChange={(e) => setCardSearch({ ...cardSearch, [item.key]: e.target.value })}
                                placeholder={`Search ${allItems.length} records…`}
                                data-testid={`search-${item.key}`}
                                className="w-full h-7 pl-7 pr-6 text-[11px] bg-slate-50 border border-slate-200 rounded focus:border-[#0052FF] focus:bg-white focus:outline-none"
                              />
                              {cardSearch[item.key] && (
                                <button
                                  onClick={() => setCardSearch({ ...cardSearch, [item.key]: '' })}
                                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-700"
                                  aria-label="Clear search"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          )}
                          {q && (
                            <p className="text-[10px] text-slate-500 px-1" data-testid={`search-result-${item.key}`}>
                              {items.length === 0 ? 'No matches' : `${items.length} of ${allItems.length} match "${q}"`}
                            </p>
                          )}
                          {(q ? items : (expandedCards[item.key] ? items : items.slice(0, 5))).map((rec, i) => (
                            <div key={rec.id || i} className="flex items-center justify-between text-xs bg-slate-50 px-2.5 py-1.5 rounded group">
                              <div className="min-w-0 flex-1">
                                <span className="font-medium text-slate-800 truncate">{rec.name || rec.shift_name || rec.code || 'Item'}</span>
                                {rec.code && <span className="ml-2 text-slate-400 font-mono">{rec.code}</span>}
                                {item.key === 'employee_categories' && Number(rec.wage_period_start_day) > 1 && (
                                  <span data-testid={`wage-period-chip-${i}`}
                                    title={`Wage period: ${ordSuffix(rec.wage_period_start_day)} of previous month → ${ordSuffix(rec.wage_period_end_day)} of current month`}
                                    className="ml-2 text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5 whitespace-nowrap">
                                    {ordSuffix(rec.wage_period_start_day)} → {ordSuffix(rec.wage_period_end_day)}
                                  </span>
                                )}
                              </div>
                              {canEdit && (
                                <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                                  <button
                                    onClick={() => handleEdit(item, rec)}
                                    data-testid={`edit-${item.key}-${i}`}
                                    title="Edit"
                                    className="p-1 text-slate-500 hover:text-[#0052FF] hover:bg-white rounded"
                                  >
                                    <Edit2 className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(item.collection, rec.id)}
                                    data-testid={`delete-${item.key}-${i}`}
                                    title="Delete"
                                    className="p-1 text-slate-400 hover:text-red-500 hover:bg-white rounded"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                          {!q && items.length > 5 && (
                            <button
                              onClick={() => setExpandedCards({ ...expandedCards, [item.key]: !expandedCards[item.key] })}
                              data-testid={`toggle-expand-${item.key}`}
                              className="w-full text-[11px] text-[#0052FF] hover:text-[#0043D1] font-medium py-1 hover:bg-slate-50 rounded"
                            >
                              {expandedCards[item.key] ? `Show less ▲` : `Show ${items.length - 5} more ▼`}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Add / Edit Modal — Stage 73.19e: widened to max-w-2xl with capped
          90vh + scrollable body so the Salary Head form (and any other
          master with many fields) always fits in the viewport regardless
          of screen size. Header + footer are pinned via flex column. */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="master-add-modal">
          <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-5 border-b bg-slate-50 rounded-t-xl flex-shrink-0">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  {editingId ? `Edit ${activeMaster?.name}` : `Add ${activeMaster?.name}`}
                </h2>
                <p className="text-xs text-slate-500">
                  {editingId ? 'Update this master record' : 'Create a new master record'}
                </p>
              </div>
              <button onClick={() => { setShowModal(false); setEditingId(null); }} data-testid="master-modal-close"><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Name *</label>
                <input data-testid="master-name-input" value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter name" className="w-full border-2 border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#0052FF]" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Code</label>
                <input data-testid="master-code-input" value={formData.code}
                  onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="Auto-generated if empty" className="w-full border-2 border-slate-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[#0052FF]" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Description</label>
                <textarea data-testid="master-desc-input" value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  rows={2} placeholder="Optional description" className="w-full border-2 border-slate-200 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-[#0052FF]" />
              </div>

              {/* Wage Period — ONLY for Employee Categories. Drives attendance upload mapping. */}
              {activeMaster?.key === 'employee_categories' && (
                <div className="border-t border-slate-200 pt-4 space-y-3" data-testid="category-wage-period-fields">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-700">Wage Period (attendance cycle)</p>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Leave blank for the standard calendar month (1st → last day). For a cross-month cycle,
                    set both days — e.g. Start 21 / End 20 means "21st of previous month → 20th of current month".
                    All attendance uploads automatically map this category's employees to their wage period.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: 'Calendar month', s: '', e: '' },
                      { label: '21st → 20th', s: 21, e: 20 },
                      { label: '26th → 25th', s: 26, e: 25 },
                      { label: '15th → 14th', s: 15, e: 14 },
                    ].map(p => (
                      <button key={p.label} type="button"
                        data-testid={`wage-preset-${p.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                        onClick={() => setFormData({ ...formData, wage_period_start_day: p.s, wage_period_end_day: p.e })}
                        className="px-2.5 py-1 text-[11px] rounded-full border border-slate-300 text-slate-600 hover:border-indigo-400 hover:text-indigo-700 transition-colors">
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Start Day (1–31)</label>
                      <input type="number" min="1" max="31" data-testid="wage-period-start-day"
                        value={formData.wage_period_start_day}
                        onChange={e => setFormData({ ...formData, wage_period_start_day: e.target.value })}
                        placeholder="e.g. 21"
                        className="w-full border-2 border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#0052FF]" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">End Day (1–31)</label>
                      <input type="number" min="1" max="31" data-testid="wage-period-end-day"
                        value={formData.wage_period_end_day}
                        onChange={e => setFormData({ ...formData, wage_period_end_day: e.target.value })}
                        placeholder="e.g. 20"
                        className="w-full border-2 border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#0052FF]" />
                    </div>
                  </div>
                  {Number(formData.wage_period_start_day) > 1 && Number(formData.wage_period_end_day) >= 1 && (
                    <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="wage-period-preview">
                      {Number(formData.wage_period_start_day) > Number(formData.wage_period_end_day)
                        ? `Wage period: ${ordSuffix(formData.wage_period_start_day)} of previous month → ${ordSuffix(formData.wage_period_end_day)} of current month. Example for April payroll: ${ordSuffix(formData.wage_period_start_day)} Mar → ${ordSuffix(formData.wage_period_end_day)} Apr.`
                        : `Wage period: ${ordSuffix(formData.wage_period_start_day)} → ${ordSuffix(formData.wage_period_end_day)} of the same month.`}
                    </p>
                  )}
                </div>
              )}

              {/* Extra fields ONLY for Salary Heads — drives the payroll engine */}
              {activeMaster?.key === 'salary_heads' && (
                <div className="border-t border-slate-200 pt-4 space-y-3" data-testid="salary-head-extra-fields">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">⚙️ Salary head behaviour (drives the payroll engine)</p>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Head Type</label>
                    <select value={formData.head_type}
                      onChange={(e) => setFormData({ ...formData, head_type: e.target.value })}
                      data-testid="master-head-type"
                      className="w-full border-2 border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#0052FF]">
                      <option value="earning">Earning (Basic / HRA / Bonus / etc.)</option>
                      <option value="deduction">Deduction (PF / ESI / PT / TDS / etc.)</option>
                      <option value="employer_contribution">Employer Contribution (PF_ER / ESI_ER / etc.)</option>
                      <option value="reimbursement">Reimbursement (Fuel / Travel / etc.)</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <Toggle label="Attendance-linked"
                      hint="Prorate by payable days (Basic / HRA usually ON; Bonus usually OFF)"
                      checked={formData.is_attendance_linked}
                      onChange={(v) => setFormData({ ...formData, is_attendance_linked: v })}
                      testId="master-flag-attendance" />
                    <Toggle label="Part of CTC"
                      hint="Drives the Bulk Custom CTC Upload template. ON → this head appears as a column there. OFF → excluded (typical for variable inputs like Incentive/OT)."
                      checked={formData.is_part_of_ctc}
                      onChange={(v) => setFormData({ ...formData, is_part_of_ctc: v })}
                      testId="master-flag-ctc" />
                    <Toggle label="Monthly Input"
                      hint="ON → this head appears as a column in the Monthly Inputs grid + CSV upload template. Use for variable per-cycle items (Incentive, OT, Arrears, Reimbursement, ad-hoc deductions). Mutually intentional with 'Part of CTC' — turn ON for variable items, OFF for structural CTC heads."
                      checked={formData.is_monthly_input}
                      onChange={(v) => setFormData({ ...formData, is_monthly_input: v })}
                      testId="master-flag-monthly-input" />
                    <Toggle label="Part of Gross"
                      hint="Adds to gross pay shown on payslip"
                      checked={formData.is_part_of_gross}
                      onChange={(v) => setFormData({ ...formData, is_part_of_gross: v })}
                      testId="master-flag-gross" />
                    <Toggle label="Affects Net Pay"
                      hint="Earnings: adds to net. Deductions: subtracts from net."
                      checked={formData.affects_net_pay}
                      onChange={(v) => setFormData({ ...formData, affects_net_pay: v })}
                      testId="master-flag-net" />
                    <Toggle label="Taxable"
                      hint="Included in TDS computation"
                      checked={formData.is_taxable}
                      onChange={(v) => setFormData({ ...formData, is_taxable: v })}
                      testId="master-flag-taxable" />
                    <Toggle label="Show on Payslip"
                      hint="Appears as a line item on the employee's payslip PDF"
                      checked={formData.show_on_payslip}
                      onChange={(v) => setFormData({ ...formData, show_on_payslip: v })}
                      testId="master-flag-payslip" />
                    <Toggle label="PF Applicable"
                      hint="Wage base for PF computation (typically Basic + DA)"
                      checked={formData.pf_applicable}
                      onChange={(v) => setFormData({ ...formData, pf_applicable: v })}
                      testId="master-flag-pf" />
                    <Toggle label="ESI Applicable"
                      hint="Counts toward ESI gross-wage threshold"
                      checked={formData.esi_applicable}
                      onChange={(v) => setFormData({ ...formData, esi_applicable: v })}
                      testId="master-flag-esi" />
                    <Toggle label="PT Applicable"
                      hint="Counts toward Professional Tax slabs"
                      checked={formData.pt_applicable}
                      onChange={(v) => setFormData({ ...formData, pt_applicable: v })}
                      testId="master-flag-pt" />
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 p-5 border-t bg-slate-50 rounded-b-xl flex-shrink-0">
              <Button variant="outline" onClick={() => setShowModal(false)} data-testid="master-modal-cancel">Cancel</Button>
              <Button data-testid="master-modal-save" onClick={handleSave} disabled={saving} className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editingId ? 'Update' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

// Compact toggle row — used in the Salary Head fieldset.
const Toggle = ({ label, hint, checked, onChange, testId }) => (
  <label className="flex items-start gap-2 cursor-pointer text-xs select-none p-2 rounded hover:bg-slate-50">
    <input
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      data-testid={testId}
      className="mt-0.5 w-4 h-4 accent-[#0052FF] cursor-pointer"
    />
    <span className="flex-1">
      <span className="font-medium text-slate-800 block">{label}</span>
      {hint && <span className="text-[10px] text-slate-500 leading-tight block">{hint}</span>}
    </span>
  </label>
);

export default Masters;
